import { DateTime, SystemZone } from "luxon";
import {
  CarrierDto,
  CarrierListDto,
  GetCarrierDto,
} from "mock-api/src/shared-v2-types";
import {
  Brand,
  getApiDetailsDecorator,
  joinPath,
  parseDateTime,
} from "src/utils";
import { BaseCarrier } from "./base-carrier.model";
import { CarrierType } from "./carrier-type.model";

const api = getApiDetailsDecorator<CarrierListDto>();

export class Carrier extends BaseCarrier {
  protected constructor(args: Omit<ClassProperties<Carrier>, "carrierId">) {
    super(args);
    this.createdDate = args.createdDate;
    this.modifiedDate = args.modifiedDate;
    this.type = args.type;
    this.id = args.id;

    this.carrierId = args.id;
  }

  /** The time zone used by the carrier models (local/system time). */
  public static readonly timeZone = SystemZone.instance;

  public readonly carrierId: CarrierReference["id"];

  @api() public readonly createdDate: DateTime;
  @api() public readonly modifiedDate: DateTime | null;
  @api({ key: "carrierType" }) public readonly type: CarrierType;
  @api() public readonly id: CarrierReference["id"];

  public static deserialize(data: CarrierListDto): Carrier {
    const timeZone = this.timeZone;

    return new Carrier({
      createdDate: parseDateTime(data.createdDate, { timeZone }),
      key: data.carrierKey,
      modifiedDate: data.modifiedDate
        ? parseDateTime(data.modifiedDate, { timeZone })
        : null,
      name: data.name,
      id: data.id,
      type: CarrierType.deserialize(data.carrierType),
    });
  }

  public static deserializeCarrierDto(data: CarrierDto): Carrier {
    const timeZone = this.timeZone;

    return new Carrier({
      createdDate: parseDateTime(data.createdDate, { timeZone }),
      key: data.carrierKey,
      modifiedDate: parseDateTime(data.modifiedDate, { timeZone }),
      name: data.name,
      id: data.id,
      type: CarrierType.deserialize(data.carrierType),
    });
  }

  public static deserializeList(value: CarrierListDto[]): readonly Carrier[] {
    return value.map((x) => Carrier.deserialize(x));
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getCarrierRouteUrl(this, ...childPaths);
  }
}

export class CarrierDetails extends BaseCarrier {
  protected constructor(
    args: Omit<ClassProperties<CarrierDetails>, "carrierId">,
  ) {
    super(args);
    this.comments = args.comments;
    this.contactEmails = args.contactEmails;
    this.isActive = args.isActive;
    this.typeId = args.typeId;
    this.id = args.id;

    this.carrierId = args.id;
  }

  public readonly comments: string | null;
  public readonly contactEmails: string | null;
  public readonly isActive: boolean;
  public readonly typeId: number;
  public readonly id: CarrierReference["id"];

  public readonly carrierId: CarrierReference["id"];

  public static deserialize(data: GetCarrierDto): CarrierDetails {
    return new CarrierDetails({
      comments: data.comments,
      key: data.carrierKey,
      name: data.name,
      id: data.id,
      typeId: data.carrierTypeId,
      contactEmails: data.appointmentNotificationEmails,
      isActive: data.active,
    });
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getCarrierRouteUrl(this, ...childPaths);
  }
}

export function getCarrierRouteUrl(
  carrier: CarrierReference,
  ...childPaths: string[]
): string {
  return joinPath("/global/carriers", String(carrier.id), ...childPaths);
}

export interface CarrierReference {
  readonly id: Brand<number, "global-carrier">;
}

export function deserializeSimpleCarrier(
  data: Pick<CarrierDto, "id" | "name">,
): SimpleCarrier {
  return {
    getRouteUrl: (...childPaths) =>
      getCarrierRouteUrl({ id: data.id }, ...childPaths),
    // Duplicate ID to match the PartnerCarrier signature so we can use both
    // interchangeably.
    carrierId: data.id,
    id: data.id,
    name: data.name,
  };
}

export interface SimpleCarrier extends Pick<
  Carrier,
  "id" | "name" | "carrierId"
> {
  getRouteUrl(...childPaths: string[]): string;
}
